import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button, CircularProgress } from '@mui/material';

import Hubspot from 'main/common/Hubspot';
import TextAreaInput from 'pages/Landings/sections/Company/Contacts/components/Sales/components/TextAreaInput';
import { logger } from 'services/logger';
import gtagEvent from 'utils/gtag-event';

import useStyles from './styles';

import TextInput from '../TextInput';

interface Props {
    onSuccess?: () => void;
    onFailure?: () => void;
    className?: string;
}

interface FormData {
    firstname: string;
    email: string;
    phone?: string;
    message: string;
}

const LOADING_TIMEOUT = 1_000;

const ContactSalesForm: React.FC<Props> = ({ onSuccess, onFailure, className }) => {
    const { classes, cx } = useStyles();
    const [isLoading, setIsLoading] = useState(false);

    const { register, formState, handleSubmit } = useForm<FormData>();
    const { errors } = formState;

    const onSubmit = useCallback(
        async (data: FormData) => {
            try {
                setIsLoading(true);
                await new Promise<void>((resolve) =>
                    setTimeout(() => {
                        Hubspot.send(Hubspot.LANDING_FEED_FORM, data);
                        gtagEvent('contact_sales_submit');
                        resolve();
                    }, LOADING_TIMEOUT)
                );
                onSuccess?.();
            } catch (error) {
                logger.error(error);
                onFailure?.();
            } finally {
                setIsLoading(false);
            }
        },
        [onFailure, onSuccess]
    );

    return (
        <div className={cx(classes.root, className)}>
            <div className={classes.text}>
                <h1 className={classes.title}>{'Get in touch with sales'}</h1>
                <p className={classes.description}>
                    {'Our Sales team will reach out to discuss customized solutions for your business.'}
                    &nbsp;
                    {'Simply fill out the form below!'}
                </p>
            </div>
            <div className={classes.fields}>
                <TextInput
                    label="Name"
                    type="text"
                    required
                    placeholder="Enter name"
                    helperText={errors.firstname ? errors.firstname.message ?? 'Name is not valid' : undefined}
                    disabled={isLoading}
                    {...register('firstname', {
                        required: {
                            value: true,
                            message: 'Required field'
                        }
                    })}
                />
                <TextInput
                    label="Email"
                    type="email"
                    required
                    placeholder="Enter email"
                    helperText={errors.email ? errors.email.message ?? 'Email is not valid' : undefined}
                    disabled={isLoading}
                    {...register('email', {
                        required: {
                            value: true,
                            message: 'Required field'
                        },
                        pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/
                    })}
                />
                <TextInput
                    label="Phone"
                    type="tel"
                    placeholder="Enter phone"
                    helperText={errors.phone ? errors.phone.message ?? 'Phone is not valid' : undefined}
                    disabled={isLoading}
                    {...register('phone')}
                />
                <TextAreaInput
                    type="text"
                    placeholder="Hi! I would like to learn more about the Enterprise plans"
                    helperText={errors.message ? errors.message.message ?? 'Name is not valid' : undefined}
                    disabled={isLoading}
                    {...register('message', {
                        required: {
                            value: true,
                            message: 'Required field'
                        }
                    })}
                />
            </div>
            <Button
                variant="contained"
                disableElevation
                disableFocusRipple
                onClick={handleSubmit(onSubmit)}
                disabled={isLoading}
                className={classes.button}
                endIcon={isLoading ? <CircularProgress size={16} thickness={6} color="inherit" /> : null}
            >
                {'Submit'}
            </Button>
        </div>
    );
};

export default ContactSalesForm;
